// @ts-check
// Lista dos fluxos de automação (/campaigns/flows; spec 2.0.0, fluxos.md seção 20.1; contrato 09 de fluxos, seção 17):
// cabeçalho com "Exportar relatório", "Modelos" e "Criar fluxo"; busca e filtros na URL; tabela com colunas ordenáveis,
// seleção em lote e o menu de cada linha; no celular, cartões. A lista inteira vem de uma vez (no máximo 300 fluxos) e
// o filtro e a ordem são as mesmas regras do servidor (client/src/lib/flow-list.js).
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { createFlow, downloadFlowsReport, duplicateFlow, listFlows } from '../../data/flows.js';
import { Button } from '../../ui/button.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { useDebouncedValue, useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { downloadBlob } from '../../lib/csv.js';
import { formatDateTime, formatNumber, formatTime, getFormatPreferences } from '../../lib/format.js';
import {
  FLOW_LIST_CHANNELS, FLOW_LIST_STATUSES, activeFlowFilterCount, defaultOrderOf, filterAndSortFlows, flowEditorHref, flowListQueryParams,
  flowNameHref, flowStatsHref, flowStatsRange, readFlowListQuery, relativeDayOf, showsInFlowCount,
} from '../../lib/flow-list.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowStatusChip } from './flow-ui.js';
import { FlowMenuButton } from './flow-menu.js';
import { FlowNameDialog } from './flow-name-dialog.js';
import { FlowCollectionDialog, FlowDeactivateDialog, FlowDeleteDialog } from './flow-lifecycle-dialogs.js';
import { openFlowInsertLeadsDialog } from './flow-insert-leads-dialog.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/flow-list.js').FlowSummary} FlowSummary
 * @typedef {import('../../lib/flow-list.js').FlowListQuery} FlowListQuery
 * @typedef {import('../../lib/flow-list.js').FlowSort} FlowSort
 * @typedef {{ kind: 'deactivate' | 'delete' | 'collection', flows: FlowSummary[] } | { kind: 'create' } | null} ListDialog
 */

const SKELETON_ROWS = 5;
const TEMPLATES_PATH = '/campaigns/flows/templates';

/** "Hoje, 08:09", "Ontem, 17:40" ou a data e a hora. @param {string} instant */
export function lastChangeText(instant) {
  const relative = relativeDayOf(instant, new Date(), getFormatPreferences().timeZone);
  if (relative === 'TODAY') return t('flows.list.today', { time: formatTime(instant) });
  if (relative === 'YESTERDAY') return t('flows.list.yesterday', { time: formatTime(instant) });
  return formatDateTime(instant);
}

/** Baixa o relatório dos fluxos dos últimos 30 dias. */
async function exportReport() {
  try {
    const range = flowStatsRange('LAST_30_DAYS', new Date(), getFormatPreferences().timeZone);
    const file = await downloadFlowsReport(range);
    downloadBlob(file.blob, file.fileName ?? `fluxos-${range.from}-a-${range.to}.csv`);
  } catch (error) {
    showErrorSnackbar(error);
  }
}

export function FlowsPage() {
  const isMobile = useIsMobile();
  const query = readFlowListQuery(searchParamsState.value);
  const [flows, setFlows] = useState(/** @type {FlowSummary[] | null} */ (null));
  const [collections, setCollections] = useState(/** @type {string[]} */ ([]));
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  const [selectedIds, setSelectedIds] = useState(/** @type {Set<string>} */ (new Set()));
  const [dialog, setDialog] = useState(/** @type {ListDialog} */ (null));
  const [search, setSearch] = useState(query.q);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const load = () => {
    setLoadError(null);
    listFlows().then((result) => {
      setFlows(result.flows);
      setCollections(result.collections);
      setSelectedIds((current) => new Set([...current].filter((id) => result.flows.some((flow) => flow.id === id))));
    }).catch((error) => {
      setLoadError(error);
      setFlows(null);
    });
  };
  useEffect(() => {
    document.title = t('flows.list.title');
    load();
  }, []);
  useEffect(() => {
    if (debouncedSearch !== query.q) updateSearchParams({ q: debouncedSearch || null });
  }, [debouncedSearch]);

  /** @param {Partial<FlowListQuery>} changes */
  const setQuery = (changes) => {
    const next = { ...query, ...changes };
    const parameters = flowListQueryParams(next);
    updateSearchParams(parameters);
    if (changes.q !== undefined) setSearch(changes.q);
  };
  const visible = useMemo(() => (flows ? filterAndSortFlows(flows, query) : []), [flows, searchParamsState.value]);
  const selected = visible.filter((flow) => selectedIds.has(flow.id));
  const hasFilters = query.q !== '' || activeFlowFilterCount(query) > 0;

  /** @param {FlowSummary} flow */
  const duplicate = async (flow) => {
    try {
      const copy = await duplicateFlow(flow.id);
      navigate(flowEditorHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  /** @param {FlowSummary} flow */
  const rowMenuItems = (flow) => [
    { key: 'edit', text: t('flows.list.menu.edit'), icon: 'IconPencil', onSelect: () => navigate(flowEditorHref(flow.id)) },
    flow.status === 'ACTIVE'
      ? { key: 'deactivate', text: t('flows.list.menu.deactivate'), icon: 'IconPlayerPause', onSelect: () => setDialog({ kind: 'deactivate', flows: [flow] }) }
      : { key: 'activate', text: t('flows.list.menu.activate'), icon: 'IconPlayerPlay', onSelect: () => navigate(flowEditorHref(flow.id, { activate: true })) },
    { key: 'duplicate', text: t('flows.list.menu.duplicate'), icon: 'IconCopy', onSelect: () => duplicate(flow) },
    { key: 'stats', text: t('flows.list.menu.stats'), icon: 'IconChartBar', onSelect: () => navigate(flowStatsHref(flow.id)) },
    ...(flow.status === 'ACTIVE'
      ? [{ key: 'insert', text: t('flows.list.menu.insert'), icon: 'IconUserPlus', onSelect: () => openFlowInsertLeadsDialog({ flow: { id: flow.id, name: flow.name }, onDone: load }) }]
      : []),
    { key: 'collection', text: t('flows.list.menu.collection'), icon: 'IconFolder', onSelect: () => setDialog({ kind: 'collection', flows: [flow] }) },
    { key: 'delete', text: t('flows.list.menu.delete'), icon: 'IconTrash', accent: /** @type {const} */ ('danger'), separatorBefore: true, onSelect: () => setDialog({ kind: 'delete', flows: [flow] }) },
  ];
  /** @param {string} id @param {boolean} isSelected */
  const toggleSelected = (id, isSelected) => setSelectedIds((current) => {
    const next = new Set(current);
    if (isSelected) next.add(id);
    else next.delete(id);
    return next;
  });
  const closeDialog = () => setDialog(null);
  const afterBulk = () => {
    setDialog(null);
    setSelectedIds(new Set());
    load();
  };

  const hasFlows = Boolean(flows && flows.length > 0);
  const headerActions = isMobile
    ? html`<${Button} title=${t('flows.list.create')} icon="IconPlus" accent="blue" size="small" onClick=${() => setDialog({ kind: 'create' })} />
      <${FlowMenuButton} ariaLabel=${t('flows.list.moreActions')} icon="IconDotsVertical" items=${[
        ...(hasFlows ? [{ key: 'templates', text: t('flows.list.templates'), icon: 'IconLayoutGrid', onSelect: () => navigate(TEMPLATES_PATH) }] : []),
        { key: 'export', text: t('flows.list.export'), icon: 'IconFileExport', onSelect: exportReport },
      ]} />`
    : html`<${Button} title=${t('flows.list.export')} icon="IconFileExport" variant="tertiary" size="small" onClick=${exportReport} />
      ${hasFlows ? html`<${Button} title=${t('flows.list.templates')} icon="IconLayoutGrid" variant="secondary" size="small" onClick=${() => navigate(TEMPLATES_PATH)} />` : null}
      <${Button} title=${t('flows.list.create')} icon="IconPlus" accent="blue" size="small" onClick=${() => setDialog({ kind: 'create' })} data-testid="flow-create-button" />`;

  /** @type {any} */
  let body;
  if (loadError) {
    body = html`<div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${t('flows.list.loadError')}
      action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${load} />`} /></div>`;
  } else if (flows === null) {
    body = html`<div class="flows-table-wrapper" aria-busy="true">
      ${Array.from({ length: SKELETON_ROWS }, (_, index) => html`<div key=${index} class="flows-skeleton-row"><${Skeleton} width="40%" /><${Skeleton} width="12%" /><${Skeleton} width="10%" /><${Skeleton} width="18%" /></div>`)}
    </div>`;
  } else if (flows.length === 0) {
    body = html`<div class="flows-state flows-empty" data-testid="flows-empty">
      <span class="flows-empty-icon" aria-hidden="true"><${Icon} name="IconSitemap" size=${32} /></span>
      <h2 class="flows-empty-title">${t('flows.list.emptyTitle')}</h2>
      <p class="flows-empty-text">${t('flows.list.emptyText')}</p>
      <div class="flows-empty-actions">
        <${Button} title=${t('flows.list.startFromTemplate')} icon="IconLayoutGrid" accent="blue" size="small" onClick=${() => navigate(TEMPLATES_PATH)} />
        <${Button} title=${t('flows.list.createBlank')} variant="secondary" size="small" onClick=${() => setDialog({ kind: 'create' })} />
      </div>
    </div>`;
  } else {
    body = html`
      ${selected.length > 0
        ? html`<${FlowBulkBar} count=${selected.length} hasActive=${selected.some((flow) => flow.status === 'ACTIVE')}
            onDeactivate=${() => setDialog({ kind: 'deactivate', flows: selected.filter((flow) => flow.status === 'ACTIVE') })}
            onCollection=${() => setDialog({ kind: 'collection', flows: selected })} onDelete=${() => setDialog({ kind: 'delete', flows: selected })}
            onClear=${() => setSelectedIds(new Set())} />`
        : null}
      ${visible.length === 0
        ? html`<div class="flows-state" data-testid="flows-no-results">
            <${EmptyState} icon="IconSearch" title=${query.q ? t('flows.list.noResultsFor', { query: query.q }) : t('flows.list.noResults')}
              action=${html`<${Button} title=${t('flows.list.clearFilters')} variant="secondary" size="small"
                onClick=${() => {
                  setSearch('');
                  updateSearchParams({ q: null, status: null, collection: null, channel: null });
                }} />`} />
          </div>`
        : isMobile
          ? html`<ul class="flows-cards">${visible.map((flow) => html`<${FlowCard} key=${flow.id} flow=${flow} menuItems=${rowMenuItems(flow)} />`)}</ul>`
          : html`<${FlowsTable} flows=${visible} query=${query} selectedIds=${selectedIds} onSort=${(/** @type {FlowSort} */ sort) => setQuery({
              sort, order: query.sort === sort ? (query.order === 'asc' ? 'desc' : 'asc') : defaultOrderOf(sort),
            })}
            onToggle=${toggleSelected} onToggleAll=${(/** @type {boolean} */ isSelected) => setSelectedIds(isSelected ? new Set(visible.map((flow) => flow.id)) : new Set())}
            menuItemsOf=${rowMenuItems} />`}`;
  }

  return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.list.title')} actions=${headerActions} />`}>
    <div class="flows-list" data-testid="flows-page">
      ${hasFlows || hasFilters
        ? html`<${FlowListToolbar} query=${query} search=${search} onSearch=${setSearch} collections=${collections} onChange=${setQuery} />`
        : null}
      ${body}
    </div>
    ${dialog?.kind === 'create'
      ? html`<${FlowNameDialog} mode="create" collections=${collections} existingNames=${(flows ?? []).map((flow) => flow.name)} onClose=${closeDialog}
          onSubmit=${async (/** @type {{ name: string, collection: string | null }} */ value) => {
            try {
              const flow = await createFlow({ name: value.name, collection: value.collection });
              navigate(flowEditorHref(flow.id));
              return true;
            } catch (error) {
              showErrorSnackbar(error);
              return false;
            }
          }} />`
      : null}
    ${dialog?.kind === 'deactivate' ? html`<${FlowDeactivateDialog} flows=${dialog.flows} onClose=${closeDialog} onDone=${afterBulk} />` : null}
    ${dialog?.kind === 'delete' ? html`<${FlowDeleteDialog} flows=${dialog.flows} onClose=${closeDialog} onDone=${afterBulk} />` : null}
    ${dialog?.kind === 'collection'
      ? html`<${FlowCollectionDialog} flows=${dialog.flows} collections=${collections} initialCollection=${dialog.flows.length === 1 ? dialog.flows[0].collection : null}
          onClose=${closeDialog} onDone=${afterBulk} />`
      : null}
  <//>`;
}

/**
 * Busca, "Filtros (n)" e os chips dos filtros ligados.
 * @param {{ query: FlowListQuery, search: string, onSearch: (value: string) => void, collections: string[], onChange: (changes: Partial<FlowListQuery>) => void }} props
 */
function FlowListToolbar({ query, search, onSearch, collections, onChange }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const count = activeFlowFilterCount(query);
  /** @param {string} status */
  const toggleStatus = (status) => onChange({ statuses: query.statuses.includes(status) ? query.statuses.filter((value) => value !== status) : [...query.statuses, status] });
  /** @type {Array<{ key: string, label: string, onRemove: () => void }>} */
  const chips = [
    ...query.statuses.map((status) => ({ key: `status-${status}`, label: t(`flows.status.${status}`), onRemove: () => toggleStatus(status) })),
    ...(query.collection ? [{ key: 'collection', label: query.collection, onRemove: () => onChange({ collection: null }) }] : []),
    ...(query.channel ? [{ key: 'channel', label: t(`flows.list.channels.${query.channel}`), onRemove: () => onChange({ channel: null }) }] : []),
  ];
  return html`<div class="flows-toolbar">
    <div class="flows-toolbar-row">
      <label class="flows-search">
        <${Icon} name="IconSearch" size=${16} />
        <input type="search" class="flows-search-input" value=${search} placeholder=${t('flows.list.search')} aria-label=${t('flows.list.search')}
          onInput=${(/** @type {InputEvent} */ event) => onSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <button type="button" ref=${anchorRef} class="flows-filter-button" aria-haspopup="dialog" aria-expanded=${isOpen ? 'true' : 'false'}
        data-active=${count > 0 ? 'true' : undefined} onClick=${() => setIsOpen(!isOpen)}>
        <${Icon} name="IconFilter" size=${16} />
        <span>${count > 0 ? t('flows.list.filtersCount', { count }) : t('flows.list.filters')}</span>
      </button>
      <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-start" width=${260} role="dialog">
        <div class="flows-filter-menu" aria-label=${t('flows.list.filters')}>
          <fieldset class="flows-filter-group">
            <legend>${t('flows.list.filterStatus')}</legend>
            ${FLOW_LIST_STATUSES.map((status) => html`<label key=${status} class="flows-filter-option">
              <${Checkbox} checked=${query.statuses.includes(status)} onChange=${() => toggleStatus(status)} ariaLabel=${t(`flows.status.${status}`)} />
              <span>${t(`flows.status.${status}`)}</span>
            </label>`)}
          </fieldset>
          ${collections.length > 0
            ? html`<fieldset class="flows-filter-group">
                <legend>${t('flows.list.filterCollection')}</legend>
                <select class="text-input" value=${query.collection ?? ''} aria-label=${t('flows.list.filterCollection')}
                  onChange=${(/** @type {Event} */ event) => onChange({ collection: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                  <option value="">${t('flows.list.anyCollection')}</option>
                  ${collections.map((collection) => html`<option key=${collection} value=${collection}>${collection}</option>`)}
                </select>
              </fieldset>`
            : null}
          <fieldset class="flows-filter-group">
            <legend>${t('flows.list.filterChannel')}</legend>
            ${FLOW_LIST_CHANNELS.map((channel) => html`<label key=${channel} class="flows-filter-option">
              <input type="radio" name="flows-filter-channel" checked=${query.channel === channel}
                onChange=${() => onChange({ channel: /** @type {'EMAIL' | 'WHATSAPP'} */ (channel) })} />
              <span>${t(`flows.list.channels.${channel}`)}</span>
            </label>`)}
            ${query.channel ? html`<button type="button" class="flow-link-button" onClick=${() => onChange({ channel: null })}>${t('flows.list.anyChannel')}</button>` : null}
          </fieldset>
        </div>
      <//>
    </div>
    ${chips.length > 0
      ? html`<div class="flows-filter-chips" aria-label=${t('flows.list.activeFilters')}>
          ${chips.map((chip) => html`<span key=${chip.key} class="flows-filter-chip">
            <span>${chip.label}</span>
            <button type="button" aria-label=${t('flows.list.removeFilter', { filter: chip.label })} onClick=${chip.onRemove}><${Icon} name="IconX" size=${12} /></button>
          </span>`)}
        </div>`
      : null}
  </div>`;
}

/**
 * Barra da seleção em lote: Desativar, Mover para coleção e Excluir (Ativar precisa do modal de revisão, um por vez).
 * @param {{ count: number, hasActive: boolean, onDeactivate: () => void, onCollection: () => void, onDelete: () => void, onClear: () => void }} props
 */
function FlowBulkBar({ count, hasActive, onDeactivate, onCollection, onDelete, onClear }) {
  return html`<div class="flows-bulk-bar" role="region" aria-label=${t('flows.list.selection')}>
    <span class="flows-bulk-count">${t('flows.list.selected', { count })}</span>
    <${Button} title=${t('flows.list.menu.deactivate')} icon="IconPlayerPause" variant="secondary" size="small" disabled=${!hasActive} onClick=${onDeactivate} />
    <${Button} title=${t('flows.list.moveToCollection')} icon="IconFolder" variant="secondary" size="small" onClick=${onCollection} />
    <${Button} title=${t('flows.list.menu.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${onDelete} />
    <${Button} title=${t('flows.list.clearSelection')} variant="tertiary" size="small" onClick=${onClear} />
  </div>`;
}

const COLUMNS = /** @type {Array<{ sort: FlowSort, labelKey: string, numeric?: boolean }>} */ ([
  { sort: 'name', labelKey: 'flows.list.columns.name' },
  { sort: 'status', labelKey: 'flows.list.columns.status' },
  { sort: 'entered', labelKey: 'flows.list.columns.entered', numeric: true },
  { sort: 'inFlow', labelKey: 'flows.list.columns.inFlow', numeric: true },
  { sort: 'createdAt', labelKey: 'flows.list.columns.createdAt' },
  { sort: 'updatedAt', labelKey: 'flows.list.columns.updatedAt' },
]);

/**
 * Nome do fluxo com o chip da coleção e o alerta de pendências.
 * @param {{ flow: FlowSummary }} props
 */
function FlowNameCell({ flow }) {
  return html`<div class="flows-name-cell">
    <span class="flows-name-line">
      <${Link} href=${flowNameHref(flow)} className="flows-name-link">${flow.name}<//>
      ${flow.problemCount > 0
        ? html`<span class="flows-problem-icon" title=${t('flows.list.problems', { count: flow.problemCount })} aria-label=${t('flows.list.problems', { count: flow.problemCount })}>
            <${Icon} name="IconAlertTriangle" size=${14} />
          </span>`
        : null}
    </span>
    ${flow.collection ? html`<span class="flows-collection-chip">${flow.collection}</span>` : null}
  </div>`;
}

/**
 * @param {{ flows: FlowSummary[], query: FlowListQuery, selectedIds: Set<string>, onSort: (sort: FlowSort) => void,
 *   onToggle: (id: string, isSelected: boolean) => void, onToggleAll: (isSelected: boolean) => void, menuItemsOf: (flow: FlowSummary) => any[] }} props
 */
function FlowsTable({ flows, query, selectedIds, onSort, onToggle, onToggleAll, menuItemsOf }) {
  const selectedCount = flows.filter((flow) => selectedIds.has(flow.id)).length;
  return html`<div class="flows-table-wrapper">
    <table class="flows-table" data-testid="flows-table">
      <thead>
        <tr>
          <th class="flows-table-check" scope="col">
            <${Checkbox} checked=${selectedCount > 0 && selectedCount === flows.length} indeterminate=${selectedCount > 0 && selectedCount < flows.length}
              ariaLabel=${t('flows.list.selectAll')} onChange=${(/** @type {boolean} */ checked) => onToggleAll(checked)} />
          </th>
          ${COLUMNS.map((column) => {
            const isSorted = query.sort === column.sort;
            return html`<th key=${column.sort} scope="col" data-numeric=${column.numeric ? 'true' : undefined}
              aria-sort=${isSorted ? (query.order === 'asc' ? 'ascending' : 'descending') : 'none'}>
              <button type="button" class="flows-sort-button" data-sorted=${isSorted ? 'true' : undefined} onClick=${() => onSort(column.sort)}>
                <span>${t(column.labelKey)}</span>
                <${Icon} name=${isSorted ? (query.order === 'asc' ? 'IconArrowUp' : 'IconArrowDown') : 'IconArrowsSort'} size=${12} />
              </button>
            </th>`;
          })}
          <th class="flows-table-menu" scope="col"><span class="visually-hidden-text">${t('flows.list.columns.actions')}</span></th>
        </tr>
      </thead>
      <tbody>
        ${flows.map((flow) => html`<tr key=${flow.id} data-selected=${selectedIds.has(flow.id) ? 'true' : undefined} data-testid="flow-row">
          <td class="flows-table-check">
            <${Checkbox} checked=${selectedIds.has(flow.id)} ariaLabel=${t('flows.list.selectFlow', { name: flow.name })}
              onChange=${(/** @type {boolean} */ checked) => onToggle(flow.id, checked)} />
          </td>
          <td><${FlowNameCell} flow=${flow} /></td>
          <td><${FlowStatusChip} status=${flow.status} inactiveReason=${flow.inactiveReason} /></td>
          <td data-numeric="true">${formatNumber(flow.entered)}</td>
          <td data-numeric="true">${showsInFlowCount(flow) ? formatNumber(flow.inFlow) : '-'}</td>
          <td class="flows-table-date">${formatDateTime(flow.createdAt)}</td>
          <td class="flows-table-date">${lastChangeText(flow.updatedAt)}</td>
          <td class="flows-table-menu">
            <${FlowMenuButton} ariaLabel=${t('flows.list.rowMenu', { name: flow.name })} items=${menuItemsOf(flow)} className="flow-menu-trigger flows-row-menu" />
          </td>
        </tr>`)}
      </tbody>
    </table>
  </div>`;
}

/**
 * Cartão do celular: nome, situação, "Entraram 1.240 · Agora 312" e o menu.
 * @param {{ flow: FlowSummary, menuItems: any[] }} props
 */
function FlowCard({ flow, menuItems }) {
  return html`<li class="flows-card" data-testid="flow-card">
    <div class="flows-card-main">
      <${FlowNameCell} flow=${flow} />
      <div class="flows-card-meta">
        <${FlowStatusChip} status=${flow.status} inactiveReason=${flow.inactiveReason} />
        <span>${t('flows.list.cardCounts', { entered: formatNumber(flow.entered), now: showsInFlowCount(flow) ? formatNumber(flow.inFlow) : '-' })}</span>
      </div>
    </div>
    <${FlowMenuButton} ariaLabel=${t('flows.list.rowMenu', { name: flow.name })} items=${menuItems} className="flow-menu-trigger flows-row-menu" />
  </li>`;
}
